import React from "react"
import { Box, Heading, ListItem, OrderedList, Text, UnorderedList } from "@chakra-ui/react"

import { useCore } from "@hooks/useCore"
import Link from "../Link"
import ArticleQuote from "../Article/ArticleQuote"
import Table from "@app/components/Table"
import { useRoutes } from "@hooks/useRoutes"

export interface ArticleContentProps {
  rawContent?: GatsbyTypes.SanityBlock
}

const Content = ({ rawContent }: ArticleContentProps) => {
  const { urlResolver } = useRoutes()
  const {
    helpers: { sanityContent },
  } = useCore()

  const customSerializers = {
    types: {
      block: ({ node }: any) => {
        const { style, children, markDefs } = node
        const text = children[0]?.text || ""
        const isHeading = style.match(/h[0-9{1}]/g)

        const markLink = markDefs?.filter(mark => (mark._key && mark._type === "link") || mark._type === "document")

        const getContent = (child: Record<string, string>) => {
          const linkArr = markLink?.filter(m => child.marks.includes(m?._key))
          const to = linkArr[0]?.document ? urlResolver(linkArr[0]?.document)?.url : linkArr[0]?.link?.link

          if (linkArr.length) {
            return (
              <Link
                external={linkArr[0].link?.external}
                to={to}
                title={linkArr[0].link?.title}
                variant="underline"
                textDecoration={"underline"}
              >
                {child.text}
              </Link>
            )
          }
          return child.text
        }

        if (isHeading)
          return (
            <Heading as={style} size={style} fontWeight="semibold" mb={4} mx={[2.5, 0]}>
              {children.map((child: Record<string, string>, key: number) => (
                <React.Fragment key={key}>{getContent(child)}</React.Fragment>
              ))}
            </Heading>
          )
        else if (style === "normal")
          return (
            <Text mb={4} mx={[2.5, 0]}>
              {children.map((child: Record<string, string>, key: number) => (
                <React.Fragment key={key}>{getContent(child)}</React.Fragment>
              ))}
            </Text>
          )
        else if (style === "blockquote") return <ArticleQuote>{text}</ArticleQuote>
        return <></>
      },
      table: ({ node: articleTable }) => {
        return (
          <Box>
            <Table table={articleTable} />
          </Box>
        )
      },
    },
    list: ({ type, children }: any) => {
      const items = children.map((child: any) => (
        <ListItem key={child._key} mx={[2.5, 0]}>
          {child.props?.children?.map(child => (
            <Text key={child?._key} display={`inline`}>
              {child}
            </Text>
          ))}
        </ListItem>
      ))

      return type === "bullet" ? (
        <UnorderedList mb={4} marginInlineStart={3}>
          {items}
        </UnorderedList>
      ) : (
        <OrderedList mb={4} marginInlineStart={3}>
          {items}
        </OrderedList>
      )
    },
  }

  const content = sanityContent(rawContent, customSerializers)

  return <Box>{content}</Box>
}

export default React.memo(Content)
